import { useState, useEffect, useCallback, useRef } from 'react';
import { cn } from '@c2fo/liquidity';
import { ShrinkLeftIcon } from '@c2fo/liquidity/icons';
import { Breadcrumb } from './Breadcrumb';
import { handleClick } from './core';
import { useNavigationContext, useNavigationItems } from './NavigationContext';
import RecentAwardsDrawer from './RecentAwardsDrawer';
import { useReactRouterNavigation } from './useReactRouterNavigation';
import UserAvatarMenu from './UserMenu/UserAvatarMenu';

type ScrollDirection = 'up' | 'down' | 'idle';

interface PositionData {
  direction: ScrollDirection;
  isSticking: boolean;
  height: number;
}

const THROTTLE_DELAY = 16; // ~60fps throttling

const NavigationTop = () => {
  const { isNavCollapsed, setIsNavCollapsed, isNavCollapseAllowed } = useNavigationContext();
  const navigationItems = useNavigationItems();
  const { getBreadcrumbs } = useReactRouterNavigation();
  const breadcrumbs = getBreadcrumbs(navigationItems);
  const containerRef = useRef<HTMLDivElement>(null);
  const lastScrollY = useRef<number>(0);
  const throttleTimeout = useRef<NodeJS.Timeout | null>(null);
  const isThrottled = useRef<boolean>(false);
  const [positionData, setPositionData] = useState<PositionData>({
    direction: 'idle',
    isSticking: false,
    height: containerRef.current?.offsetHeight ?? 0,
  });

  const handleScroll = useCallback(() => {
    if (isThrottled.current) return;

    isThrottled.current = true;
    throttleTimeout.current = setTimeout(() => {
      isThrottled.current = false;
    }, THROTTLE_DELAY);

    const currentScrollY = window.scrollY;
    const scrollDiff = currentScrollY - lastScrollY.current;

    // check if component is sticking (scrolled past its original position)
    const containerHeight = containerRef.current?.offsetHeight ?? 0;
    const isCurrentlySticking = currentScrollY > containerHeight;

    setPositionData((prev) => {
      let newDirection: ScrollDirection = prev.direction;

      // always show navigation when at or near the top of the page
      if (currentScrollY <= 10) {
        newDirection = 'idle';
      } else if (Math.abs(scrollDiff) > 5) {
        newDirection = scrollDiff > 0 ? 'down' : 'up';
      }

      return {
        ...prev,
        direction: newDirection,
        isSticking: isCurrentlySticking,
        height: containerHeight,
      };
    });

    lastScrollY.current = currentScrollY;
  }, []);

  useEffect(() => {
    // initialize lastScrollY with current scroll position
    lastScrollY.current = window.scrollY;

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);

      if (throttleTimeout.current) {
        clearTimeout(throttleTimeout.current);
      }
    };
  }, [handleScroll]);

  const onClickCollapseNav = () => {
    if (!isNavCollapseAllowed) {
      return;
    }

    setIsNavCollapsed(!isNavCollapsed);
  };

  return (
    <div
      ref={containerRef}
      className={cn('hidden bg-canvas/75 backdrop-blur-sm transition-all duration-500 ease-in-out md:block', {
        '-translate-y-full': positionData.direction === 'down',
        'translate-y-0': positionData.direction === 'up' || positionData.direction === 'idle',
      })}
    >
      <div className="mx-auto flex h-16 max-w-screen-xl items-center justify-between gap-6 px-6">
        <div className="flex items-center overflow-hidden">
          {isNavCollapseAllowed && (
            <button className="-ml-3 p-3" onClick={(e) => handleClick(e, onClickCollapseNav)} type="button">
              <ShrinkLeftIcon
                className={cn('shrink-0', {
                  'rotate-180': isNavCollapsed,
                })}
              />
            </button>
          )}
          <div className="flex min-w-0 flex-1 items-center overflow-hidden">
            <Breadcrumb primary={breadcrumbs.primary} sub={breadcrumbs.sub} />
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-4">
          <RecentAwardsDrawer variant="desktop" />
          <UserAvatarMenu variant="desktop" />
        </div>
      </div>
    </div>
  );
};

export default NavigationTop;
